import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, FlaskConical, History, Pencil, Plus, RefreshCw, Search, Sparkles, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AdminPanel from "@/components/admin/AdminPanel";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { request } from "@/lib/api";

const ANY_GENDER = "__any__";
const GENDERS = [
  { value: ANY_GENDER, label: "Everyone", genders: [] },
  { value: "male", label: "Male only", genders: ["male"] },
  { value: "female", label: "Female only", genders: ["female"] },
];

const money = (v) => (v === null || v === undefined ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
const emptyOffer = { client_id: "", display_name: "", price: "", cost_price: "", gender: ANY_GENDER, note: "" };
const emptyTest = { name: "", code: "", category: "", cost_price: "", list_price: "" };

export default function PackageManager({ token, open = true, onClose, inline = false }) {
  const [tab, setTab] = useState("packages");
  const [data, setData] = useState(null);
  const [tests, setTests] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  // view: list | offer | history | composition | test
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOffer);
  const [history, setHistory] = useState(null);
  const [composition, setComposition] = useState(null);
  const [chosenTests, setChosenTests] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [testForm, setTestForm] = useState(emptyTest);
  const [editingTestId, setEditingTestId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [catalogue, testMaster] = await Promise.all([
        request("/admin/packages", {}, token),
        request("/admin/tests", {}, token),
      ]);
      setData(catalogue);
      setTests(testMaster);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    load();
    setError("");
    setNotice("");
    setView("list");
    setTab("packages");
  }, [open, load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    if (!term) return data.packages;
    return data.packages.filter(
      (p) =>
        (p.provider_package_name || "").toLowerCase().includes(term) ||
        (p.agreement_name || "").toLowerCase().includes(term) ||
        p.offers.some((o) => o.display_name.toLowerCase().includes(term) || o.client_name.toLowerCase().includes(term)),
    );
  }, [data, search]);

  const filteredTests = useMemo(() => {
    if (!tests) return [];
    const term = search.trim().toLowerCase();
    if (!term) return tests.tests;
    return tests.tests.filter((t) => t.name.toLowerCase().includes(term) || (t.code || "").toLowerCase().includes(term));
  }, [tests, search]);

  const belowCost = useMemo(
    () => (data ? data.packages.flatMap((p) => p.offers).filter((o) => o.is_loss) : []),
    [data],
  );

  // ---- pricing suggestion, recalculated as tests are picked ----
  useEffect(() => {
    if (view !== "offer" || !chosenTests.length) {
      setSuggestion(null);
      return;
    }
    let cancelled = false;
    request("/admin/tests/price-suggestion", { method: "POST", body: JSON.stringify({ test_ids: chosenTests }) }, token)
      .then((result) => !cancelled && setSuggestion(result))
      .catch(() => !cancelled && setSuggestion(null));
    return () => {
      cancelled = true;
    };
  }, [chosenTests, view, token]);

  function openOffer(pkg, offer) {
    setError("");
    setEditing({ pkg, offer, mode: offer ? "edit" : pkg ? "map" : "in_house" });
    setForm({
      client_id: offer?.client_id || data?.clients?.[0]?.client_id || "",
      display_name: offer?.display_name || pkg?.provider_package_name || "",
      price: offer ? String(offer.our_price) : "",
      cost_price:
        offer?.cost_price != null ? String(offer.cost_price) : pkg?.provider_cost != null ? String(pkg.provider_cost) : "",
      gender: offer?.applicable_genders?.length === 1 ? offer.applicable_genders[0] : ANY_GENDER,
      note: "",
    });
    setChosenTests([]);
    setSuggestion(null);
    setView("offer");
  }

  async function saveOffer(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const body = {
      display_name: form.display_name,
      price: Number(form.price),
      cost_price: form.cost_price === "" ? null : Number(form.cost_price),
      applicable_genders: GENDERS.find((g) => g.value === form.gender)?.genders ?? [],
      is_active: editing.offer ? editing.offer.is_active : true,
      note: form.note || null,
    };
    try {
      let saved;
      if (editing.mode === "in_house") {
        saved = await request("/admin/packages/in-house", { method: "POST", body: JSON.stringify({ ...body, client_id: form.client_id }) }, token);
      } else if (editing.mode === "map") {
        saved = await request(`/admin/packages/${editing.pkg.provider_package_id}/clients/${form.client_id}`, { method: "PUT", body: JSON.stringify(body) }, token);
      } else {
        saved = await request(`/admin/packages/offers/${editing.offer.id}`, { method: "PUT", body: JSON.stringify(body) }, token);
      }
      // A package built from chosen tests records what it contains.
      if (chosenTests.length && saved?.id) {
        await request(`/admin/packages/offers/${saved.id}/tests`, { method: "PUT", body: JSON.stringify({ test_ids: chosenTests }) }, token);
      }
      await load();
      setView("list");
      setEditing(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function openHistory(offer) {
    setError("");
    try {
      setHistory(await request(`/admin/packages/offers/${offer.id}/history`, {}, token));
      setView("history");
    } catch (e) {
      setError(e.message);
    }
  }

  async function openComposition(offer) {
    setError("");
    try {
      const current = await request(`/admin/packages/offers/${offer.id}/tests`, {}, token);
      setComposition(current);
      setChosenTests(current.tests.map((t) => t.test_id));
      setView("composition");
    } catch (e) {
      setError(e.message);
    }
  }

  async function saveComposition() {
    setBusy(true);
    setError("");
    try {
      const updated = await request(
        `/admin/packages/offers/${composition.package.id}/tests`,
        { method: "PUT", body: JSON.stringify({ test_ids: chosenTests }) },
        token,
      );
      setComposition(updated);
      setNotice(`${updated.test_count} tests saved for ${updated.package.display_name}.`);
      await load();
      setView("list");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveTest(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const body = {
      name: testForm.name,
      code: testForm.code || null,
      category: testForm.category || null,
      cost_price: testForm.cost_price === "" ? null : Number(testForm.cost_price),
      list_price: testForm.list_price === "" ? null : Number(testForm.list_price),
      is_active: true,
    };
    try {
      if (editingTestId) await request(`/admin/tests/${editingTestId}`, { method: "PUT", body: JSON.stringify(body) }, token);
      else await request("/admin/tests", { method: "POST", body: JSON.stringify(body) }, token);
      await load();
      setView("list");
      setTestForm(emptyTest);
      setEditingTestId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleVisible(offer, isActive) {
    setError("");
    try {
      await request(`/admin/packages/offers/${offer.id}/visibility`, { method: "PUT", body: JSON.stringify({ is_active: isActive }) }, token);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function refreshCosts() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await request("/admin/packages/sync-provider-costs", { method: "POST" }, token);
      setNotice(
        r.updated
          ? `${r.updated} package${r.updated === 1 ? "" : "s"} had their provider cost updated.` +
            (r.now_selling_below_cost.length ? ` ${r.now_selling_below_cost.length} are now below cost.` : "")
          : "Every provider cost was already up to date.",
      );
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function toggleTest(testId) {
    setChosenTests((current) =>
      current.includes(testId) ? current.filter((id) => id !== testId) : [...current, testId],
    );
  }

  const priceNum = form.price === "" ? null : Number(form.price);
  const costNum = form.cost_price === "" ? null : Number(form.cost_price);
  const formLoss = priceNum !== null && costNum !== null && priceNum < costNum;

  return (
    <AdminPanel inline={inline} open={open} onClose={onClose} title="Catalogue — providers, packages and what each organisation pays" testId="admin-package-manager">

        {error && <p className="form-error" role="alert" data-testid="package-manager-error">{error}</p>}
        {notice && <p className="profile-saved" role="status" data-testid="package-notice">{notice}</p>}

        {view === "list" && (
          <>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="packages" data-testid="tab-packages">Packages ({data?.packages?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="tests" data-testid="tab-tests">Test master ({tests?.tests?.length ?? 0})</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="package-toolbar">
              <div className="employee-search">
                <Search size={15} />
                <Input placeholder={tab === "packages" ? "Search package, agreement or organisation" : "Search test or code"} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="package-search" />
              </div>
              {tab === "packages" ? (
                <>
                  <Button variant="outline" onClick={refreshCosts} disabled={busy} data-testid="package-refresh-costs">
                    <RefreshCw size={15} />{busy ? "Checking…" : "Refresh provider costs"}
                  </Button>
                  <Button onClick={() => openOffer(null, null)} data-testid="package-new-in-house"><Plus size={15} />Own package</Button>
                </>
              ) : (
                <Button onClick={() => { setTestForm(emptyTest); setEditingTestId(null); setView("test"); }} data-testid="test-new"><Plus size={15} />New test</Button>
              )}
            </div>

            {tab === "packages" && belowCost.length > 0 && (
              <p className="package-loss-warning" data-testid="package-below-cost-banner">
                <AlertTriangle size={15} />
                {belowCost.length} package{belowCost.length === 1 ? " is" : "s are"} priced below what they cost us.
              </p>
            )}

            {tab === "packages" && (
              <div className="package-table-wrap">
                <Table data-testid="package-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider / package</TableHead>
                      <TableHead>Their price</TableHead>
                      <TableHead>Tests</TableHead>
                      <TableHead>Sold to</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((pkg) => {
                      const key = pkg.provider_package_id ?? pkg.offers[0]?.id;
                      const isOpen = Boolean(expanded[key]);
                      return [
                        <TableRow key={`m-${key}`} className="package-master-row" data-testid={`package-row-${key}`}>
                          <TableCell>
                            <button type="button" className="package-disclosure" onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))} data-testid={`package-expand-${key}`}>
                              {pkg.offers.length ? (isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />) : <span className="package-disclosure__spacer" />}
                              <span>
                                <span className="package-name">{pkg.provider_package_name}</span>
                                <small className="employee-muted">
                                  {pkg.provider_name}{pkg.agreement_name ? ` · ${pkg.agreement_name}` : ""}
                                </small>
                              </span>
                            </button>
                          </TableCell>
                          <TableCell>{money(pkg.provider_cost)}</TableCell>
                          <TableCell className="employee-muted">{pkg.provider_test_count ?? "—"}</TableCell>
                          <TableCell>
                            {pkg.offers.length ? (
                              <span className="package-org-chips">
                                {pkg.offers.map((o) => <Badge key={o.id} variant={o.is_active ? "secondary" : "outline"}>{o.client_name}</Badge>)}
                              </span>
                            ) : (
                              <span className="employee-muted">Not sold to anyone</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {pkg.kind === "provider" && (
                              <Button variant="outline" size="sm" onClick={() => openOffer(pkg, null)} data-testid={`package-map-${key}`}>Add to org</Button>
                            )}
                          </TableCell>
                        </TableRow>,
                        ...(isOpen
                          ? pkg.offers.map((offer) => (
                              <TableRow key={offer.id} className="package-offer-row" data-testid={`package-offer-${offer.id}`}>
                                <TableCell className="package-offer-cell">
                                  <Badge variant="secondary">{offer.client_name}</Badge>
                                  <span className="employee-muted"> sells as “{offer.display_name}”</span>
                                  {offer.applicable_genders?.length > 0 && <small className="employee-muted"> · {offer.applicable_genders.join(", ")} only</small>}
                                </TableCell>
                                <TableCell>
                                  <small className="employee-muted">our cost</small>
                                  <div>{money(offer.cost_price)}</div>
                                </TableCell>
                                <TableCell>
                                  <Button variant="ghost" size="sm" onClick={() => openComposition(offer)} data-testid={`package-tests-${offer.id}`}>
                                    <FlaskConical size={13} />{offer.test_count || 0}
                                  </Button>
                                </TableCell>
                                <TableCell>
                                  <small className="employee-muted">sells for</small>
                                  <div>
                                    {money(offer.our_price)}{" "}
                                    {offer.margin !== null && (
                                      <span className={offer.is_loss ? "package-loss" : "package-margin"} data-testid={`package-margin-${offer.id}`}>
                                        {offer.is_loss && <AlertTriangle size={12} />}
                                        {offer.is_loss ? `−${money(Math.abs(offer.margin)).slice(1)}` : `+${money(offer.margin).slice(1)}`}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="package-actions">
                                  <Switch checked={offer.is_active} onCheckedChange={(v) => toggleVisible(offer, v)} data-testid={`package-visible-${offer.id}`} />
                                  <Button variant="ghost" size="sm" onClick={() => openHistory(offer)} title="Price history" data-testid={`package-history-${offer.id}`}><History size={14} /></Button>
                                  <Button variant="ghost" size="sm" onClick={() => openOffer(pkg, offer)} title="Edit" data-testid={`package-edit-${offer.id}`}><Pencil size={14} /></Button>
                                </TableCell>
                              </TableRow>
                            ))
                          : []),
                      ];
                    })}
                    {!rows.length && <TableRow><TableCell colSpan={5} className="employee-muted">Nothing matches. If this is empty, run the Apollo sync first.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}

            {tab === "tests" && (
              <div className="package-table-wrap">
                <Table data-testid="test-table">
                  <TableHeader>
                    <TableRow><TableHead>Test</TableHead><TableHead>Code</TableHead><TableHead>Category</TableHead><TableHead>Costs us</TableHead><TableHead>Lists at</TableHead><TableHead /></TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTests.map((t) => (
                      <TableRow key={t.test_id} data-testid={`test-row-${t.test_id}`}>
                        <TableCell><span className="package-name">{t.name}</span></TableCell>
                        <TableCell className="employee-muted">{t.code || "—"}</TableCell>
                        <TableCell className="employee-muted">{t.category || "—"}</TableCell>
                        <TableCell>{money(t.cost_price)}</TableCell>
                        <TableCell>{money(t.list_price)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" data-testid={`test-edit-${t.test_id}`} onClick={() => {
                            setTestForm({ name: t.name, code: t.code || "", category: t.category || "", cost_price: t.cost_price ?? "", list_price: t.list_price ?? "" });
                            setEditingTestId(t.test_id);
                            setView("test");
                          }}><Pencil size={14} /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredTests.length && <TableRow><TableCell colSpan={6} className="employee-muted">No tests yet. Add the tests you buy, with their prices, and packages can be priced from them.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {view === "offer" && editing && (
          <form className="package-edit" onSubmit={saveOffer} data-testid="package-edit-form">
            <div className="upload-summary">
              <strong>{editing.mode === "in_house" ? "New package of our own" : editing.pkg?.provider_package_name}</strong>
              {editing.pkg?.agreement_name && <span>{editing.pkg.agreement_name}</span>}
              <Button type="button" variant="ghost" size="sm" onClick={() => { setView("list"); setEditing(null); }} data-testid="package-edit-cancel"><X size={14} />Back</Button>
            </div>

            <div className="package-price-compare">
              <div><span className="profile-field__label">Costs us</span><strong>{costNum === null ? "—" : money(costNum)}</strong></div>
              <div><span className="profile-field__label">Org pays (dependents)</span><strong>{priceNum === null ? "—" : money(priceNum)}</strong></div>
              <div><span className="profile-field__label">Margin</span><strong className={formLoss ? "package-loss" : "package-margin"}>{priceNum === null || costNum === null ? "—" : money(priceNum - costNum)}</strong></div>
            </div>
            {formLoss && (
              <p className="package-loss-warning" data-testid="package-loss-warning">
                <AlertTriangle size={15} />This sells below cost — every dependent booking loses {money(costNum - priceNum)}.
              </p>
            )}

            {editing.mode === "in_house" && (
              <div className="package-builder" data-testid="package-builder">
                <span className="profile-field__label"><FlaskConical size={13} />Build it from the test master</span>
                <div className="package-test-picker">
                  {(tests?.tests || []).map((t) => (
                    <label key={t.test_id} className={chosenTests.includes(t.test_id) ? "test-chip is-on" : "test-chip"} data-testid={`builder-test-${t.test_id}`}>
                      <input type="checkbox" checked={chosenTests.includes(t.test_id)} onChange={() => toggleTest(t.test_id)} hidden />
                      {t.name}<small>{money(t.cost_price)}</small>
                    </label>
                  ))}
                  {!tests?.tests?.length && <span className="employee-muted">No tests in the master yet — add some on the Test master tab.</span>}
                </div>
                {suggestion && (
                  <div className="package-suggestion" data-testid="package-suggestion">
                    <Sparkles size={15} />
                    <div>
                      <strong>Recommended: {money(suggestion.suggested_selling_price)}</strong>
                      <small className="employee-muted">
                        {suggestion.test_count} tests · costs {money(suggestion.suggested_cost_price)} ·
                        {" "}à la carte {money(suggestion.sum_of_list_prices)} less {Math.round((suggestion.bundle_discount || 0) * 100)}% bundle discount
                        {suggestion.missing_list_for.length > 0 && ` · no list price for ${suggestion.missing_list_for.join(", ")}`}
                      </small>
                    </div>
                    <Button type="button" variant="outline" size="sm" data-testid="package-apply-suggestion" onClick={() => setForm((f) => ({
                      ...f,
                      price: suggestion.suggested_selling_price != null ? String(suggestion.suggested_selling_price) : f.price,
                      cost_price: suggestion.suggested_cost_price != null ? String(suggestion.suggested_cost_price) : f.cost_price,
                    }))}>Use these</Button>
                  </div>
                )}
              </div>
            )}

            <label htmlFor="pkg-client">Organisation</label>
            <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))} disabled={editing.mode === "edit"}>
              <SelectTrigger id="pkg-client" data-testid="package-form-client"><SelectValue /></SelectTrigger>
              <SelectContent>{(data?.clients || []).map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {editing.mode !== "edit" && <small className="employee-muted">The same package can be sold to several organisations, each at its own agreed price.</small>}

            <label htmlFor="pkg-name">Name employees see</label>
            <Input id="pkg-name" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} required data-testid="package-form-name" />

            <label htmlFor="pkg-cost">Cost price (₹) — what it costs us</label>
            <Input id="pkg-cost" type="number" min="0" step="1" value={form.cost_price} onChange={(e) => setForm((f) => ({ ...f, cost_price: e.target.value }))} data-testid="package-form-cost" />
            {editing.pkg && editing.mode !== "in_house" && <small className="employee-muted">{editing.pkg.provider_name} currently charges {money(editing.pkg.provider_cost)}. Change it only if this org's agreement differs.</small>}

            <label htmlFor="pkg-price">Selling price (₹) — what a dependent pays</label>
            <Input id="pkg-price" type="number" min="0" step="1" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} required data-testid="package-form-price" />
            <small className="employee-muted">Employees are always covered and pay nothing.</small>

            <label htmlFor="pkg-gender">Who can book it</label>
            <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
              <SelectTrigger id="pkg-gender" data-testid="package-form-gender"><SelectValue /></SelectTrigger>
              <SelectContent>{GENDERS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
            </Select>

            <label htmlFor="pkg-note">Reason for this price (kept in history)</label>
            <Input id="pkg-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. FY26-27 agreement" data-testid="package-form-note" />

            <div className="upload-actions">
              <Button type="button" variant="ghost" onClick={() => { setView("list"); setEditing(null); }}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.client_id || form.price === ""} data-testid="package-form-save">
                {busy ? "Saving…" : editing.mode === "edit" ? "Save changes" : "Add to organisation"}
              </Button>
            </div>
          </form>
        )}

        {view === "composition" && composition && (
          <div className="package-edit" data-testid="package-composition">
            <div className="upload-summary">
              <strong>{composition.package.display_name}</strong>
              <span>{composition.package.client_id} · sells {money(composition.package.our_price)}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setView("list")} data-testid="composition-back"><X size={14} />Back</Button>
            </div>
            <span className="profile-field__label"><FlaskConical size={13} />What this package contains</span>
            <div className="package-test-picker">
              {(tests?.tests || []).map((t) => (
                <label key={t.test_id} className={chosenTests.includes(t.test_id) ? "test-chip is-on" : "test-chip"} data-testid={`composition-test-${t.test_id}`}>
                  <input type="checkbox" checked={chosenTests.includes(t.test_id)} onChange={() => toggleTest(t.test_id)} hidden />
                  {t.name}<small>{money(t.cost_price)}</small>
                </label>
              ))}
            </div>
            {suggestion === null && composition.suggested_cost_price !== null && (
              <p className="employee-muted">These tests cost us {money(composition.suggested_cost_price)} in total.</p>
            )}
            <div className="upload-actions">
              <Button type="button" variant="ghost" onClick={() => setView("list")}>Cancel</Button>
              <Button type="button" onClick={saveComposition} disabled={busy} data-testid="composition-save">{busy ? "Saving…" : `Save ${chosenTests.length} tests`}</Button>
            </div>
          </div>
        )}

        {view === "test" && (
          <form className="package-edit" onSubmit={saveTest} data-testid="test-form">
            <div className="upload-summary">
              <strong>{editingTestId ? "Edit test" : "New test"}</strong>
              <Button type="button" variant="ghost" size="sm" onClick={() => setView("list")} data-testid="test-form-cancel"><X size={14} />Back</Button>
            </div>
            <label htmlFor="t-name">Test name</label>
            <Input id="t-name" value={testForm.name} onChange={(e) => setTestForm((f) => ({ ...f, name: e.target.value }))} required data-testid="test-form-name" />
            <label htmlFor="t-code">Code</label>
            <Input id="t-code" value={testForm.code} onChange={(e) => setTestForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. CBC" data-testid="test-form-code" />
            <label htmlFor="t-cat">Category</label>
            <Input id="t-cat" value={testForm.category} onChange={(e) => setTestForm((f) => ({ ...f, category: e.target.value }))} placeholder="e.g. Haematology" data-testid="test-form-category" />
            <label htmlFor="t-cost">Cost price (₹) — what we pay</label>
            <Input id="t-cost" type="number" min="0" step="1" value={testForm.cost_price} onChange={(e) => setTestForm((f) => ({ ...f, cost_price: e.target.value }))} data-testid="test-form-cost" />
            <label htmlFor="t-list">List price (₹) — standalone selling price</label>
            <Input id="t-list" type="number" min="0" step="1" value={testForm.list_price} onChange={(e) => setTestForm((f) => ({ ...f, list_price: e.target.value }))} data-testid="test-form-list" />
            <small className="employee-muted">Both feed the recommended price when a package is built from tests.</small>
            <div className="upload-actions">
              <Button type="button" variant="ghost" onClick={() => setView("list")}>Cancel</Button>
              <Button type="submit" disabled={busy || !testForm.name} data-testid="test-form-save">{busy ? "Saving…" : "Save test"}</Button>
            </div>
          </form>
        )}

        {view === "history" && history && (
          <div className="package-history" data-testid="package-history">
            <div className="upload-summary">
              <strong>{history.package.display_name}</strong>
              <span>{history.package.client_id} · costs {money(history.package.cost_price)} · sells {money(history.package.our_price)}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setView("list")} data-testid="package-history-back"><X size={14} />Back</Button>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>What</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>By</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.history.map((e) => (
                  <TableRow key={e.history_id}>
                    <TableCell>{new Date(e.changed_at).toLocaleString("en-IN")}</TableCell>
                    <TableCell>{e.field === "price" ? "Selling price" : "Cost price"}</TableCell>
                    <TableCell>{money(e.old_value)}</TableCell>
                    <TableCell>{money(e.new_value)}</TableCell>
                    <TableCell>{e.changed_by}{e.source === "provider_sync" && <Badge variant="outline"> provider</Badge>}</TableCell>
                    <TableCell className="employee-muted">{e.note || "—"}</TableCell>
                  </TableRow>
                ))}
                {!history.history.length && <TableRow><TableCell colSpan={6} className="employee-muted">No price changes recorded yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
    </AdminPanel>
  );
}
